import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  CognitoUserSession,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken
} from 'amazon-cognito-identity-js'
import { CognitoIdentityCredentials, CognitoIdentityServiceProvider } from 'aws-sdk'
import * as types from './mutation-types'
import { TOTP_MFA, SMS_MFA } from '@/constants'
import useLogger from '@/composables/useLogger'

const logger = useLogger('cognito')

const R = require('ramda')

const BASE_USER_ATTR_KEYS = [
  'sub',
  'email_verified',
  'name',
  'phone_number_verified',
  'email'
]

/**
 * This prevents have to do another network call for the user attributes (required for some actions)
 * The keys it picks are the *exact* keys that a call to getUserAttributes returns at the time of the writing...
 * Rather than harcoding any custom:key items, it tries to select all to remain flexible for future
 */
function extractUserAttributes (decodedIdToken) {
  return Object.assign(
    {}, // target
    R.pick(BASE_USER_ATTR_KEYS, decodedIdToken),
    R.pick(Object.keys(decodedIdToken).filter(key => key.startsWith('custom')), decodedIdToken)
  )
}

// {
//   "sub": "2a1dd42e-c974-4954-8e76-59833d3f27c4",
//   "cognito:groups": [
//     "accountadmin"
//   ],
//   "email_verified": true,
//   "iss": "https://cognito-idp.us-east-2.amazonaws.com/us-east-2_D08Mg3y18",
//   "phone_number_verified": false,
//   "cognito:username": "6cbdf181",
//   "origin_jti": "67a1395f-1a3d-4fee-8b9b-ffffffffffff",
//   "aud": "4ed9gop6j18000000000000000",
//   "event_id": "f51a322c-2165-40fe-8060-000000000000",
//   "token_use": "id",
//   "auth_time": 1667249256,
//   "name": "Doug Bryant",
//   "custom:account_id": "2",
//   "custom:account_key": "luther",
//   "exp": 0000000000,
//   "iat": 0000000000,
//   "jti": "00000000-2f19-49bd-98d4-a8ceb93d3579",
//   "email": "doug+c2@dealerapps.io"
// }
//   {
//     "ChallengeParameters": {},
//     "AuthenticationResult": {
//         "AccessToken": "eyJraWQiOiJaR3lyK3prRUF5VGN6S21GN09mTkxwbUREYzYwTnpORnFRV2JiMDN6akEwPSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiIyYTFkZDQyZS1jOTc0LTQ5NTQtOGU3Ni01OTgzM2QzZjI3YzQiLCJjb2duaXRvOmdyb3VwcyI6WyJhY2NvdW50YWRtaW4iXSwiaXNzIjoiaHR0cHM6XC9cL2NvZ25pdG8taWRwLnVzLWVhc3QtMi5hbWF6b25hd3MuY29tXC91cy1lYXN0LTJfRDA4TWczeTE4IiwiY2xpZW50X2lkIjoiNGVkOWdvcDZqMThsdm1sdDY5YTRnbzN1a24iLCJvcmlnaW5fanRpIjoiZWZmMmQ0ODctNjc2OC00M2ZmLTg3MmYtNTJlNzBmYTIwOTQ4IiwiZXZlbnRfaWQiOiI0NWQ5YTczNy02YTIwLTQ1MTYtYThkYS0wY2U2ZjVlMjA1MTAiLCJ0b2tlbl91c2UiOiJhY2Nlc3MiLCJzY29wZSI6ImF3cy5jb2duaXRvLnNpZ25pbi51c2VyLmFkbWluIiwiYXV0aF90aW1lIjoxNjY3MjQyNDE3LCJleHAiOjE2NjcyNDYwMTcsImlhdCI6MTY2NzI0MjQxNywianRpIjoiMzljMzYxNWUtMGNmZC00NDk3LWFmODItODYxMTQ1NjI2MzcyIiwidXNlcm5hbWUiOiI2Y2JkZjE4MSJ9.G1XIJwAplpldMJ6YgndLZrwJj9U9iJT-rW4U8h1xL4-AzmyGC9NHhslImkD1Yttgqq4aNne0EiD3lj21VPijGI_2YZ_L5283XKDH0MthsnWZAd4C8fEXW-jeJo6cYfUfuvx7LVip1YVDFP9wymK9y8stxdlwAbvCamHOONZAwr1F2Y8qSvVRK6_f6u28uogOd4Zcm8ibsg4WIOmDTGptFJvZ38DWsKznVISUPZfftHCF2r3KHEwt-HcxP8ccD4ejA8Q0mnjCQOjWB_NKKK4IJ0kLKltJnfCT5fBaaJOhyAqny58k5GSl7myFmxwZvs518rMqiEjRjIRAqndvjRsF7w",
//         "ExpiresIn": 3600,
//         "TokenType": "Bearer",
//         "RefreshToken": "eyJjdHkiOiJKV1QiLCJlbmMiOiJBMjU2R0NNIiwiYWxnIjoiUlNBLU9BRVAifQ.C5R0GXbcD-qMD1VJbAkJf6HyXnPueWI1sauUFINivEtIIZPrOREK_Io0OohkUBm0K3n7P1J_8071OWW-GwiwINDvRM8-iIQFxtbKwv6yUOt9Vp6JiXtwPr2wZnPr9yHNSee8TFKdXykus44Gn8444TghNSaU8-4J_gvKsmZAWBM4HJlRjRprCo9-44jlkk4oeemDA48HhQ9ox_-hgBgYdUSPkzrjpK9gxnUYgg3qK718gAU0njJC6rduHcD8xmG21RtISig8qiKZLYbYop6gRWYyLmz2aBESeGylJgq9VnQ17TtMscrL5xE2eSupxquUFZjMIvLXI0wTKJUcrGnvyQ.uCU-qy-13XG926ys.K-4oTmcfZ93A-VUTyYzwkdx19n35hUJ55oiQbItRXBHlpBT48TROMGyIhbTKYHWoILadiTox4FlGIrtmZywOmgRTn4GHMXczKyy41v9cN-aXGK7wPGXCwqKwBrg0FADpVWlT4q3_gYkw21Xn3STtgS-CK5lCU8-B2pYvcs14rHyV1uoCPOBIRZgq_DwasYmrG-JWfwCMAr7aLUvVUOQFifOx6HarYCeUxaWU2_hbhiZ3DVGfwhehq9WOiML7s4Eirxpvv_8jnffpG8dsCj7eeDuhS6DezB-LTpJzckOuRryNQwo8cbYgnicVwdIjfm9WtMI15oSDuEo4OJuGMzOhd2m1Q7GBxsAX44-pSZAW933UbIUrYKX_VyhkitMrPqrayQ5pIyJXvLgbH501uXC7nq9sMTvkz-fmOBvV6Z_o4ofuxP_D6Zwk5dQuGif3MJSosLhqtFcjoVswWI5lbRi5CVD-lwqjRNVq9K82CZMB_tgOIX7Z0l1K3I3yt-8LNbMXgJ9FXMThQz1WD0z0w8af8Zb-vBnvYKrmd6PVFCJPSA4uwUi710HHMKcvBMYLpR2Gez2bpINhdiYYPsejjF2RkFwOfcSDT7n4LjLAPSbMgbvTdzmsW13_U_ye9W-4UiW5Q-fPIV8D1gHn94JAJhkd8N19aqyEL2iDBAC5bEefBU5HLEpHa_-GrKZfAGoa4RUoaQQ3T0Wt17J9O1qvDP4NWmO7tl7mOBRducxeCSjItoBO9r43fWhVp-L1E8zfKymsp0FOTeZvltkl-cwNsD959vnZk5C0NdPCCvS8odKxrq4xOKocnNY4LANE6FL3SgHWz-HjMJjbsAJERvsreJU2E3vyyOmH6u_CJkwKbfhnV9tMd6zuUozM5_bN1IhP6x3zIabIS0zVAbZyx8Enf79_Gq8uepqGVe3ZmM6iEezStWkL-6PnHdU-YuPLAlBsrwHJNoW-rizVycGPHnEYFMubut9cZqUd2jxY21ayqMMr_48j03s2yFzdonFCJKwKbiXr6wfLbNcXqS83cUYRXcB1nKSqI4OYhFJ9J0gnjBybbeaCL2Y4VjSCyA4RqZh_YYjr0y0pLTLTnoN5e-jc5eP4_vMGYaB-7Ffu8INjJ5cgtEkKCz6Jy6BM25OnZTVhjojfCeOtj7kf4H_AXjsCh27N_MYiNDmQKH6Ljvx2YG03rordUJui2Cu6uYx23CArqHgwDYQCKHj0gJFurOe3ZZHDJaxYSjvQrFtHD924VZFe3vxl_45SPyZsNCWlEHQxerUuxq-x3CsU.NCzVXP31HbghYlyYj-vGgQ",
//         "IdToken": "eyJraWQiOiI0bmhqSDYzODNQa3dzNnZjQVVvcGpwR0lFY1pKaDJzcW5EUDNxQldwdUhvPSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiIyYTFkZDQyZS1jOTc0LTQ5NTQtOGU3Ni01OTgzM2QzZjI3YzQiLCJjb2duaXRvOmdyb3VwcyI6WyJhY2NvdW50YWRtaW4iXSwiZW1haWxfdmVyaWZpZWQiOnRydWUsImlzcyI6Imh0dHBzOlwvXC9jb2duaXRvLWlkcC51cy1lYXN0LTIuYW1hem9uYXdzLmNvbVwvdXMtZWFzdC0yX0QwOE1nM3kxOCIsInBob25lX251bWJlcl92ZXJpZmllZCI6ZmFsc2UsImNvZ25pdG86dXNlcm5hbWUiOiI2Y2JkZjE4MSIsIm9yaWdpbl9qdGkiOiJlZmYyZDQ4Ny02NzY4LTQzZmYtODcyZi01MmU3MGZhMjA5NDgiLCJhdWQiOiI0ZWQ5Z29wNmoxOGx2bWx0NjlhNGdvM3VrbiIsImV2ZW50X2lkIjoiNDVkOWE3MzctNmEyMC00NTE2LWE4ZGEtMGNlNmY1ZTIwNTEwIiwidG9rZW5fdXNlIjoiaWQiLCJhdXRoX3RpbWUiOjE2NjcyNDI0MTcsIm5hbWUiOiJEb3VnIEJyeWFudCIsImN1c3RvbTphY2NvdW50X2lkIjoiMiIsImN1c3RvbTphY2NvdW50X2tleSI6Imx1dGhlciIsImV4cCI6MTY2NzI0NjAxNywiaWF0IjoxNjY3MjQyNDE3LCJqdGkiOiI1ZDI1NTdmOS03Y2NmLTRmZTAtYTIzOC1jMGQ3MGQ0OGQ3NTciLCJlbWFpbCI6ImRvdWcrYzJAZGVhbGVyYXBwcy5pbyJ9.qjeNW4lETWKDhceUNgKGCjDquvOsRCXF2kB85SJlsuKh940MXlcF_4EmTuYhe_-PY2nzH09-KxaG6SwJwMDC1TevXPiGX6il6d4Im_7yI7wwmfvehplSb84yO2GuZjTuDqeHcr3D8RZxi6MEWy0_7YeiDY3GkTHW_iSXHrmw9wA6hH1vwLhcTJiZJG9fGvl1gcrJt8ElT7T6VqHXKgREnR1bvvUhH4x3EjD7-pSP8ntM2F-S2A9__KAfXW22Z0KMyg7ZmAFHYIbum9rMIS9guX0wYcIDz4cS2tPETaih2hCjzbD6Wl-x6JnGiYsO7_ujbdrnOAY4XgRNe0m89lRV6g"
//     }
// }

async function constructUserFromChallenge (data, userPool) {
  if (R.path(['AuthenticationResult', 'AccessToken'], data)) {
    const IdToken = new CognitoIdToken({ IdToken: data.AuthenticationResult.IdToken })
    const AccessToken = new CognitoAccessToken({ AccessToken: data.AuthenticationResult.AccessToken })
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: data.AuthenticationResult.RefreshToken })

    const userSession = new CognitoUserSession({ IdToken, AccessToken, RefreshToken })
    const userData = {
      Username: userSession.idToken.payload['cognito:username'],
      Pool: userPool
    }

    const cognitoUser = new CognitoUser(userData)
    cognitoUser.setSignInUserSession(userSession)

    const user = await new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
          return
        }

        const constructedUser = constructUser(cognitoUser, session)
        resolve(constructedUser)
      })
    })

    return user
  } else {
    throw new Error('Unable to build user from challenge')
  }
}

function constructUser (cognitoUser, session) {
  const idToken = session.getIdToken()
  const identityData = idToken.decodePayload()
  const idAttrs = extractUserAttributes(identityData)
  const userGroups = identityData['cognito:groups'] || []

  const expiryTimestamp = session.getAccessToken().getExpiration()
  const expiryDate = new Date(expiryTimestamp * 1000)

  return {
    tokenValidRemainingSeconds: () => Math.floor((expiryDate.getTime() - new Date().getTime()) / 1000),
    username: cognitoUser.getUsername(),
    tokens: {
      IdToken: idToken.getJwtToken(),
      AccessToken: session.getAccessToken().getJwtToken(),
      RefreshToken: session.getRefreshToken().getToken()
    },
    attributes: idAttrs,
    groups: userGroups,
    credentials: null // where access/secret/session tokens are stored when requested - do not fetch until needed
  }
}

// cannot use ES6 classes, the methods are not enumerable, properties are.
export default function actionsFactory (config) {
  const cognitoUserPool = new CognitoUserPool({
    UserPoolId: config.UserPoolId,
    ClientId: config.ClientId,
    Paranoia: 6
  })
  // used for credentials...
  const userPoolLoginKey = `cognito-idp.${config.Region}.amazonaws.com/${config.UserPoolId}`

  return {
    getCurrentUser ({ state, commit }) {
      return new Promise((resolve, reject) => {
        logger.trace('Checking user and if needs refresh...')
        if (state.user) {
          if (state.user.tokenValidRemainingSeconds() > (60 * 5)) {
            logger.trace('User is valid for at least 5 minutes - using Cached user...')
            resolve(state.user)
            return
          }
        }
        logger.trace('Refreshing user')

        const cognitoUser = cognitoUserPool.getCurrentUser()
        if (!cognitoUser) {
          reject(new Error('Can\'t retrieve the current user'))
          return
        }

        cognitoUser.getSession((err, session) => {
          if (err) {
            reject(err)
            return
          }
          const constructedUser = constructUser(cognitoUser, session)
          // Call AUTHENTICATE because it's utterly the same
          commit(types.AUTHENTICATE, constructedUser)
          resolve(constructedUser)
        })
      })
    },

    authenticateUser ({ dispatch, commit }, payload) {
      const authParams = {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: config.ClientId,
        AuthParameters: {
          USERNAME: payload.username,
          PASSWORD: payload.password
        }
      }

      const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({
        region: config.Region
      })

      return new Promise((resolve, reject) =>
        cognitoIdentityServiceProvider.initiateAuth(authParams, (err, data) => {
          if (err) {
            logger.trace('Error initializing Cognito Auth', err)
            reject(err)
          } else {
            // STRUCTURE OF THE DATA OBJECT
            //   {
            //     "ChallengeName": "NEW_PASSWORD_REQUIRED",
            //     "Session": "0000000000000000000000_..._kabnH4Qi54eu8HnK4RbB71Y",
            //     "ChallengeParameters": {
            //         "USER_ID_FOR_SRP": "965d724a",
            //         "requiredAttributes": "[]",
            //         "userAttributes": "{\"email_verified\":\"True\",\"name\":\"Cognito Test 2\",\"custom:account_id\":\"2\",\"custom:account_key\":\"luther\",\"email\":\"doug+c3@dealerapps.io\"}"
            //     }
            // }

            logger.trace('>>> Cognito Auth initialized', 'ChallengeName', data.ChallengeName)

            // no challenge - authenticated and good to go
            if (!data.ChallengeName) {
              // no challenge, we're good to go
              constructUserFromChallenge(data, cognitoUserPool)
                .then(constructedUser => {
                  commit(types.AUTHENTICATE, constructedUser)
                  dispatch('getUserAttributes')
                  commit(types.AUTH_CHALLENGE, null) // clear any previous challenge
                  resolve(constructedUser)
                })
              return
            }

            // save the auth challenge data for later - rest of flow needs this
            commit(types.AUTH_CHALLENGE, data)

            if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
              commit(types.AUTH_CHALLENGE, { username: payload.username })
              resolve({ newPaswordRequired: true, ...data })
              return
            }

            if ([SMS_MFA, TOTP_MFA].includes(data.ChallengeName)) {
              resolve({ mfaChallengeRequired: true, ...data })
              return
            }

            // anything else will be rejected as no code paths
            reject(new Error(`Unable to authenticate user. Unknown or unhandled challenge. ${JSON.stringify(data)}`))
          }
        })
      )
    },

    signUp ({ commit }, userInfo) {
      /* userInfo: { username, password, attributes } */
      const userAttributes = Object.keys(userInfo.attributes || {}).map(
        key =>
          new CognitoUserAttribute({
            Name: key,
            Value: userInfo.attributes[key]
          })
      )

      return new Promise((resolve, reject) => {
        cognitoUserPool.signUp(userInfo.username, userInfo.password, userAttributes, null, (err, data) => {
          if (!err) {
            commit(types.AUTHENTICATE, {
              username: data.user.getUsername(),
              tokens: null, // no session yet
              attributes: {}
            })
            resolve({ userConfirmationNecessary: !data.userConfirmed })
            return
          }
          reject(err)
        })
      })
    },

    confirmRegistration ({ state }, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username
      })

      return new Promise((resolve, reject) => {
        cognitoUser.confirmRegistration(payload.code, true, err => {
          if (!err) {
            resolve()
            return
          }
          reject(err)
        })
      })
    },

    resendConfirmationCode ({ commit }, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username
      })

      return new Promise((resolve, reject) => {
        cognitoUser.resendConfirmationCode(err => {
          if (!err) {
            resolve()
            return
          }
          reject(err)
        })
      })
    },

    forgotPassword ({ commit }, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username
      })

      return new Promise((resolve, reject) =>
        cognitoUser.forgotPassword({
          onSuccess () {
            resolve()
          },
          onFailure (err) {
            reject(err)
          }
        })
      )
    },

    confirmPassword ({ commit }, payload) {
      const cognitoUser = new CognitoUser({
        Pool: cognitoUserPool,
        Username: payload.username
      })

      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(payload.code, payload.newPassword, {
          onFailure (err) {
            reject(err)
          },
          onSuccess () {
            resolve()
          }
        })
      })
    },

    // Only for authenticated users
    changePassword ({ state }, payload) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'))
          return
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username
        })

        // Restore session without making an additional call to API
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(state.user.tokens)

        cognitoUser.changePassword(payload.oldPassword, payload.newPassword, err => {
          if (!err) {
            resolve()
            return
          }
          reject(err)
        })
      })
    },

    // Only for authenticated users
    updateAttributes ({ commit, state }, payload) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'))
          return
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username
        })

        // Restore session without making an additional call to API
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(state.user.tokens)

        const attributes = Object.keys(payload || {}).map(
          key =>
            new CognitoUserAttribute({
              Name: key,
              Value: payload[key]
            })
        )

        cognitoUser.updateAttributes(attributes, err => {
          if (!err) {
            resolve()
            return
          }
          reject(err)
        })
      })
    },

    // Only for authenticated users
    getUserAttributes ({ commit, state }) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'))
          return
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username
        })

        // Restore session without making an additional call to API
        cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(state.user.tokens)

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err)
            return
          }

          const attributesMap = (attributes || []).reduce((accum, item) => {
            accum[item.Name] = item.Value
            return accum
          }, {})

          commit(types.ATTRIBUTES, attributesMap)
          resolve(attributesMap)
        })
      })
    },

    // Only for authenticated users...
    // returns temporary account/secret/session keys for individual users
    getUserCredentials ({ commit, state }) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'))
          return
        }

        const cognitoAwsCredentials = new CognitoIdentityCredentials(
          {
            IdentityPoolId: config.IdentityPoolId,
            Logins: { [userPoolLoginKey]: state.user.tokens.IdToken },
            LoginId: state.user.username
          },
          {
            region: config.Region
          }
        )

        commit(types.CREDENTIALS, cognitoAwsCredentials)
        cognitoAwsCredentials.refresh(function (err, data) {
          if (err) {
            reject(err)
          } else {
            resolve(state.user.credentials)
          }
        })
      })
    },

    // Only for authenticated users
    signOut ({ commit, state }) {
      return new Promise((resolve, reject) => {
        // Make sure the user is authenticated
        if (state.user === null || (state.user && state.user.tokens === null)) {
          reject(new Error('User is unauthenticated'))
          return
        }

        const cognitoUser = new CognitoUser({
          Pool: cognitoUserPool,
          Username: state.user.username
        })

        cognitoUser.signOut()
        commit(types.SIGNOUT)
        resolve()
      })
    },

    // Only for authenticated users
    async setupTOTP ({ dispatch }, payload) {
      const credentials = await dispatch('getUserCredentials')
      const currentUser = await dispatch('getCurrentUser')

      const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({
        region: config.Region,
        credentials: credentials
      })

      const assocParams = {
        AccessToken: currentUser.tokens.AccessToken
      }

      return new Promise((resolve, reject) => {
        cognitoIdentityServiceProvider.associateSoftwareToken(assocParams, (err, data) => {
          if (err) {
            logger.trace('Error associating software token', err)
            reject(err)
          } else {
            resolve({ ...data, ...{ AccessToken: currentUser.tokens.AccessToken, FriendlyDeviceName: currentUser.attributes.name + ' - ' + new Date().toISOString() } })
          }
        })
      })
    },

    // Only for authenticated users
    async verifyTotpSetup ({ dispatch }, payload) {
      const credentials = await dispatch('getUserCredentials')

      const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({
        region: config.Region,
        credentials: credentials
      })

      const verifyParams = {
        UserCode: payload.UserCode,
        AccessToken: payload.AccessToken,
        FriendlyDeviceName: payload.FriendlyDeviceName
      }

      return new Promise((resolve, reject) => {
        cognitoIdentityServiceProvider.verifySoftwareToken(verifyParams, (err, data) => {
          if (err) {
            logger.trace('Error verifying software token', err)
            reject(err)
          } else {
            resolve(data)
          }
        })
      })
    },

    // Only for authenticated users
    async saveMFAPreferences ({ dispatch }, payload) {
      const credentials = await dispatch('getUserCredentials')

      const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({
        region: config.Region,
        credentials: credentials
      })
      // TODO - Parameterize for the correct type - SMS or TOTP
      const mfaPreferenceParams = {
        AccessToken: payload.AccessToken,
        SMSMfaSettings: {
          Enabled: true,
          PreferredMfa: !payload.totpIsPreferredMfa
        },
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: payload.totpIsPreferredMfa
        }
      }

      return new Promise((resolve, reject) => {
        cognitoIdentityServiceProvider.setUserMFAPreference(mfaPreferenceParams, (err, data) => {
          if (err) {
            logger.trace('Error setting MFA Preferences', err)
            reject(err)
          } else {
            resolve(data)
          }
        })
      })
    },

    async completeSignup ({ dispatch, state }, payload) {
      const challengeName = state.user_auth_challenge.ChallengeName
      if (challengeName !== 'NEW_PASSWORD_REQUIRED') {
        throw new Error('Invalid call to completeSignup, incorrect ChallengeType', challengeName)
      }
      const username = state.user_auth_challenge.ChallengeParameters.USER_ID_FOR_SRP
      const signupParams = {
        ChallengeName: challengeName,
        ClientId: config.ClientId,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: payload.password
        },
        Session: state.user_auth_challenge.Session
      }

      return dispatch('respondToAuthChallenge', signupParams)
    },

    async verifyMfaChallenge ({ dispatch }, payload) {
      const verifyParams = {
        ChallengeName: payload.ChallengeName,
        ClientId: config.ClientId,
        ChallengeResponses: {},
        Session: payload.Session
      }
      if (payload.ChallengeName === TOTP_MFA) {
        verifyParams.ChallengeResponses = {
          USERNAME: payload.username,
          SOFTWARE_TOKEN_MFA_CODE: payload.mfaChallengeUserCode
        }
      } else if (payload.ChallengeName === SMS_MFA) {
        verifyParams.ChallengeResponses = {
          USERNAME: payload.username,
          SMS_MFA_CODE: payload.mfaChallengeUserCode
        }
      } else {
        throw new Error('Unknown/Unhandled MFA Challenge')
      }
      return dispatch('respondToAuthChallenge', verifyParams)
    },

    respondToAuthChallenge ({ commit }, payload) {
      const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({
        region: config.Region
      })

      return new Promise((resolve, reject) => {
        cognitoIdentityServiceProvider.respondToAuthChallenge(payload, (err, data) => {
          if (err) {
            logger.trace('Error responding to auth challenge', err)
            reject(err)
          } else {
            // Handle case where force_password_change + needs password challenge
            if ([SMS_MFA, TOTP_MFA].includes(data.ChallengeName)) {
              resolve({ mfaChallengeRequired: true, ...data })
              return
            }

            constructUserFromChallenge(data, cognitoUserPool)
              .then(constructedUser => {
                commit(types.AUTHENTICATE, constructedUser)
                commit(types.AUTH_CHALLENGE, null) // clear any previous challenge
                resolve(constructedUser)
              })
              .catch(err => {
                logger.trace('constructUserFromChallenge error', err)
                reject(err)
              })
          }
        })
      })
    }
  }
}
